<template>
  <!-- Main Content -->
  <main class="py-4 sm:py-8 sm:p-8">
    <!-- Header -->
    <div class="mb-10 flex items-center justify-between">
      <div>
        <PageTitle>
          Gerencie seus packs
        </PageTitle>
        <PageDescription>
          Gerencie e personalize seus pacotes com facilidade.
        </PageDescription>
      </div>
      <RegisterButton @click="openNewPackageDialog">Cadastrar pack</RegisterButton>
    </div>

    <!-- Packs Table Card -->
    <TableContainer>
      <!-- Table Header -->
      <div class="mb-6 flex items-center justify-between">
        <h2 class="text-xl font-semibold text-white">Packs registrados</h2>
        
        <div class="flex items-center gap-4">
        </div>
      </div>

      <!-- Table -->
      <Table class="hidden md:block">
        <TableHeader>
          <TableRow>
            <TableHead
              v-for="header in tableHeaders"
              :key="header.key"
              :class="header.class"
            >
              <div class="flex items-center gap-2">
                {{ header.label }}
                <ArrowsUpDownIcon 
                  v-if="header.sortable"
                  class="h-4 w-4 cursor-pointer text-gray-500 hover:text-white"
                  @click="sortBy(header.key)"
                />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="packs">
            <TableFallback v-if="packs.data.length === 0" :colspan="tableHeaders.length" :icon="PackageIcon" title="Nenhum pack encontrado" :description="`Clique em 'Cadastrar pack' para começar`" />
            <TableRow
              v-for="pack in packs.data"
              :key="pack.id"
            >
              <TableCell>
                <div>
                  <div class="text-sm font-medium text-white">{{ pack.name }}</div>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex flex-wrap gap-2">
                  <Badge 
                    v-for="plan in pack.paymentPlans" 
                    :key="plan.id"
                    variant="default"
                  >
                    {{ plan.name }}
                  </Badge>
                </div>
              </TableCell>
              <TableCell>
                <StatusBadge :status="pack.status" />
              </TableCell>
              <TableCell>
                <div class="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    @click="editPackage(pack)"
                  >
                    <PencilIcon class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="hover:text-red-400"
                    @click="deletePackage(pack)"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </template>
          <TableRowSkeleton :columns="tableHeaders.length" v-else />
        </TableBody>
      </Table>

      <!-- Mobile List -->
      <div class="space-y-4 md:hidden text-left" v-if="packs">
        <template v-if="packs.data.length > 0">
          <MobileCard v-for="pack in packs.data" :key="pack.id">
            <template #header>
               <div class="flex items-center gap-2">
                 <div class="flex flex-col">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Pack</span>
                  <div class="flex items-center gap-2">
                    <span class="text-white font-bold text-lg">{{ pack.name }}</span>
                  </div>
                 </div>
               </div>
            </template>
            
            <template #actions>
               <div class="flex flex-col items-end gap-2">
                  <div class="flex items-center gap-1">
                    <button @click="editPackage(pack)" class="text-zinc-400 hover:text-white p-1">
                      <PencilIcon class="size-5" />
                    </button>
                    <button @click="deletePackage(pack)" class="text-zinc-400 hover:text-red-400 p-1">
                      <TrashIcon class="size-5" />
                    </button>
                  </div>
               </div>
            </template>
            
            <template #content>
               <MobileDataLabel label="Planos de pagamento">
                  <div class="flex flex-wrap gap-1">
                    <Badge 
                      v-for="plan in pack.paymentPlans" 
                      :key="plan.id"
                      variant="default"
                      class="text-xs"
                    >
                      {{ plan.name }}
                    </Badge>
                  </div>
               </MobileDataLabel>
               
               <div class="flex items-center justify-between pt-2">
                  <div class="flex flex-col">
                    <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Status</span>
                    <StatusBadge :status="pack.status" />
                  </div>
               </div>
            </template>
          </MobileCard>
        </template>
        <div v-else class="text-center py-12 text-zinc-500">
           <PackageIcon class="size-8 mx-auto mb-3 text-zinc-700" />
           Nenhum pack encontrado
        </div>
      </div>
      <!-- Pagination -->
      <Pagination @update:page="(page: string) => reloadAndGetAll({ page })" v-model:per-page="perPage" :data="packs?.pagination" />
    </TableContainer>
  </main>

  <DeleteConfirmationDialog v-model:open="isDeletePackageDialogOpen" :loading="isDeletingPackage" @confirm="isDeletingPackage = true; destroy(deletingPackage!).then(() => {
    toast({
      title: 'pack excluído com sucesso',
      class: 'bg-green-500 text-white',
      duration: 1500
    })
    isDeletingPackage = false
    isDeletePackageDialogOpen = false
    const index = packs?.data.findIndex(pack => pack.id == deletingPackage)
    if (index !== -1) packs?.data.splice(index!, 1)
  })" />

  <!-- New/Edit Package Dialog -->
  <Dialog v-model:open="isPackageDialogOpen">
    <DialogScrollContent class="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>{{ editingPackage ? 'Editar pack' : 'Novo pack' }}</DialogTitle>
        <DialogDescription>
          Configure as informações do pack e selecione os planos de pagamento.
        </DialogDescription>
      </DialogHeader>
      <div class="grid grid-cols-2 gap-6 py-4" v-if="!isPackageDialogLoading">
        <div class="grid gap-2 col-span-full">
          <Label>Nome do pack <span class="text-red-500">*</span></Label>
          <Input v-model="packForm.name" placeholder="Ex: Pack especial" />
        </div>
        <div class="grid gap-2 col-span-full">
          <Label>Plano <span class="text-red-500">*</span></Label>
          <TagsInput v-if="filteredPaymentPlans" class="px-0 gap-0 w-full" :model-value="paymentPlanNames">
            <div class="flex gap-2 flex-wrap items-center px-3">
              <TagsInputItem v-for="(name, index) in paymentPlanNames" :key="index" :value="name">
                <TagsInputItemText />
                <TagsInputItemDelete @click="packForm.paymentPlanIds?.splice(index, 1); paymentPlanNames.splice(index, 1)" />
              </TagsInputItem>
            </div>
            
            <ComboboxRoot v-model="paymentPlanNames" v-model:open="isPaymentPlanComboboxOpen" v-model:search-term="searchPaymentPlan" class="w-full">
              <ComboboxAnchor as-child>
                <ComboboxInput placeholder="Plano premium..." as-child>
                  <TagsInputInput @focus="isPaymentPlanComboboxOpen = true" class="w-full px-3" :class="(packForm.paymentPlanIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                </ComboboxInput>
              </ComboboxAnchor>
        
              <ComboboxPortal>
                <ComboboxContent>
                  <CommandList
                    position="popper"
                    class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                  >
                    <CommandEmpty>Nenhum plano encontrado</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        v-for="paymentPlan in filteredPaymentPlans" :key="paymentPlan.id" :value="String(paymentPlan.name)"
                        @select.prevent="(ev) => {
                          if (typeof ev.detail.value === 'string') {
                            searchPaymentPlan = ''
                            const paymentPlan = filteredPaymentPlans?.find(plan => plan.name == ev.detail.value)
                            packForm.paymentPlanIds?.push(String(paymentPlan!.id))
                            paymentPlanNames.push(paymentPlan!.name)
                          }
        
                          if (filteredPaymentPlans?.length === 0) {
                            isPaymentPlanComboboxOpen = false
                          }
                        }"
                      >
                        {{ paymentPlan.name }}
                      </CommandItem>
                    </CommandGroup>
                  </CommandList>
                </ComboboxContent>
              </ComboboxPortal>
            </ComboboxRoot>
          </TagsInput>
          <p class="text-xs text-gray-400">Você pode selecionar mais de 1 plano</p>
        </div>
        <div class="grid gap-2 col-span-full">
          <Label>Status <span class="text-red-500">*</span></Label>
          <Select v-model="packForm.status">
            <SelectTrigger>
              <SelectValue placeholder="Selecione o status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="status in PackageStatatusEnum" :value="status">{{ PackageStatusLabels[status] }}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <MultipleFilesUpload label="Mídias" :required="true" v-model:previews="previews" v-model="packForm.media" class="col-span-full" :max-files="10" />
      </div>
      <Loader2 v-else class="w-10 h-10 animate-spin text-white mx-auto" />
      <DialogFooter>
        <Button class="text-white" variant="ghost" @click="closePackageDialog">Cancelar</Button>
        <Button :disabled="isSaving || !packForm.name || (!packForm.paymentPlanIds?.length) || !packForm.status || (!packForm.media?.length && !editingPackage)" @click="save">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          {{ editingPackage ? 'Salvar alterações' : 'Cadastrar pack' }}
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import {
  ArrowsUpDownIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import MobileCard from '@/components/ui/mobile-card/MobileCard.vue'
import MobileDataLabel from '@/components/ui/mobile-card/MobileDataLabel.vue'
import {
  Dialog,
  DialogScrollContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import RegisterButton from '@/components/ui/register-button/RegisterButton.vue'
import Table from '@/components/ui/table/Table.vue'
import TableCell from '@/components/ui/table/TableCell.vue'
import TableFallback from '@/components/ui/table/TableFallback.vue'
import TableRow from '@/components/ui/table/TableRow.vue'
import TableBody from '@/components/ui/table/TableBody.vue'
import TableHeader from '@/components/ui/table/TableHeader.vue'
import TableHead from '@/components/ui/table/TableHead.vue'
import TableContainer from '@/components/ui/table/TableContainer.vue'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import TableRowSkeleton from '@/components/ui/table/TableRowSkeleton.vue'
import { usePaymentPlan } from '@/composables/usePaymentPlan'
import { PaymentPlan } from '@/interfaces/PaymentPlan'
import { CircleHelp, Loader2, Package as PackageIcon } from 'lucide-vue-next'
import { useToast } from '@/components/ui/toast'
import { TagsInput, TagsInputItem, TagsInputItemDelete, TagsInputItemText } from '@/components/ui/tags-input'
import { ComboboxAnchor, ComboboxContent, ComboboxInput, ComboboxPortal, ComboboxRoot } from 'radix-vue'
import TagsInputInput from '@/components/ui/tags-input/TagsInputInput.vue'
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { AcceptableInputValue } from 'radix-vue/dist/TagsInput/TagsInputRoot'
import DeleteConfirmationDialog from '@/components/ui/delete-dialog/DeleteConfirmationDialog.vue'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import MultipleFilesUpload from '@/components/ui/upload/MultipleFilesUpload.vue'
import { CreatePackageDTO, GetAllPackagesParams, UpdatePackageDTO, usePackage } from '@/composables/usePackage'
import { Package } from '@/interfaces/packages/Package'
import { PackageStatatusEnum, PackageStatusLabels } from '@/enums/packages/PackageStatusEnum'
import StatusBadge from '@/components/ui/badge/StatusBadge.vue'

const { toast } = useToast()
const { create, update, getAll, destroy, getById } = usePackage()
const { getAll: getAllPaymentPlans } = usePaymentPlan()
const paymentPlans = ref<IPagination<PaymentPlan>>()
// Table Configuration
const tableHeaders = [
  { key: 'name', label: 'Nome', sortable: false },
  { key: 'plans', label: 'Planos de pagamento', sortable: false },
  { key: 'status', label: 'Status', sortable: false },
  { key: 'actions', label: '', class: 'w-32' },
]
const isSaving = ref(false)
const perPage = ref('10')
const previews = ref<string[]>([])

// Filters
const filteredPaymentPlans = computed(() => paymentPlans.value?.data.filter(i => !packForm.value.paymentPlanIds?.includes(String(i.id))))
const searchPaymentPlan = ref('')
const isPaymentPlanComboboxOpen = ref(false)
// Packages data (empty for demo)
const packs = ref<IPagination<Package>>()
const paymentPlanNames = ref<string[]>([])
// Dialog and Form
const isPackageDialogOpen = ref(false)
const isPackageDialogLoading = ref(false)
const editingPackage = ref<Package | null>(null)
const packForm = ref<Partial<CreatePackageDTO | UpdatePackageDTO>>({
  name: '',
  paymentPlanIds: [],
  status: PackageStatatusEnum.ACTIVE,
  media: []
})
const deletingPackage = ref<number>()
const isDeletePackageDialogOpen = ref(false)
const isDeletingPackage = ref(false)

// Actions
const openNewPackageDialog = () => {
  previews.value = []
  editingPackage.value = null
  packForm.value = {
    name: '',
    paymentPlanIds: [],
    status: PackageStatatusEnum.ACTIVE,
    media: []
  }
  paymentPlanNames.value = []
  searchPaymentPlan.value = ''
  isPaymentPlanComboboxOpen.value = false
  isPackageDialogOpen.value = true
}

const closePackageDialog = () => {
  isPackageDialogOpen.value = false
}

const editPackage = async (pack: Package) => {
  isPackageDialogOpen.value = true
  isPackageDialogLoading.value = true
  isPaymentPlanComboboxOpen.value = false
  searchPaymentPlan.value = ''
  editingPackage.value = await getById(pack.id)
  previews.value = editingPackage.value.media?.map(media => media.source) ?? []
  isPackageDialogLoading.value = false
  packForm.value = {
    name: pack.name,
    status: pack.status,
    paymentPlanIds: ((pack.paymentPlans?.map(plan => String(plan.id)) ?? []) as unknown as AcceptableInputValue[]),
  }
  paymentPlanNames.value = pack.paymentPlans?.map(plan => plan.name) ?? []
}

const deletePackage = (pack: any) => {
  isDeletePackageDialogOpen.value = true
  deletingPackage.value = pack.id
}

const sortBy = (key: string) => {
  // Implement sorting logic
}

const save = async () => {
  isSaving.value = true

  try {
    let payload = { ...packForm.value }

    if (!editingPackage.value) {
      const pack = await create(payload as CreatePackageDTO)
      toast({
        title: 'pack criado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      packs.value = await getAll()
    } else {
      if (editingPackage.value.media.length !== previews.value.length) {
        const deletedMedia = editingPackage.value.media?.filter(media => !previews.value.includes(media.source));
        (payload as UpdatePackageDTO).deleteMediaIds = deletedMedia.map(media => media.id)
      }
      await update(editingPackage.value.id, payload as UpdatePackageDTO)
      toast({
        title: 'pack editado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })

      const _paymentPlans = paymentPlans.value?.data.filter(plan => payload.paymentPlanIds?.includes(String(plan.id))) ?? []
      const index = packs.value!.data.findIndex(pack => pack.id == editingPackage.value!.id)
      
      if (index !== -1 && index !== null) {
        packs.value!.data[index] = { 
          ...packs.value!.data[index], 
          ...packForm.value,
          paymentPlans: _paymentPlans,
        } as Package
      }
    }

    isPackageDialogOpen.value = false
  } finally {
    isSaving.value = false
  }
}

function reloadAndGetAll(params: GetAllPackagesParams) {
  packs.value = undefined
  getAll(params)
    .then(data => packs.value = data)
}

watch(() => perPage.value, () => {
  reloadAndGetAll({ perPage: perPage.value })
})

watch(() => isPackageDialogOpen.value, () => {
  if (!paymentPlans.value) {
    getAllPaymentPlans({ perPage: '100' })
      .then((data) => paymentPlans.value = data)
  }
})

onMounted(() => {
  getAll()
    .then((data) => packs.value = data)
})
</script>